import { useCallback, useMemo } from "react";

import { useAudienceActions, useCompareActions } from "@/context";
import { useServices } from "@/hooks";
import { Audience } from "@/types";
import { audiencePath } from "@/utils";

/**
 * Navigation to and between audiences. Holds no state, so it's cheap to use
 * in every card and list row.
 */
export const useAudienceLinks = (audience?: Audience) => {
  const { nav } = useServices();
  const audienceActions = useAudienceActions();
  const compareActions = useCompareActions();

  // Relative, so it opens the new audience page of the current list
  const create = useCallback(() => {
    audienceActions.create();
    nav.navigate("new");
  }, [audienceActions, nav]);

  const goTo = useCallback((segment?: "insight" | "export") => {
    if (!audience) return;
    audienceActions.load(audience);
    nav.navigate(audiencePath(audience, segment));
  }, [audience, audienceActions, nav]);

  const open = useCallback(() => goTo(), [goTo]);
  const insight = useCallback(() => goTo("insight"), [goTo]);
  const exportAudience = useCallback(() => goTo("export"), [goTo]);

  const compare = useCallback(() => {
    if (!audience) return;
    compareActions.add(audience);
    nav.navigate("/compare", { state: { audience } });
  }, [audience, compareActions, nav]);

  return useMemo(
    () => ({ create, open, insight, export: exportAudience, compare }),
    [create, open, insight, exportAudience, compare]
  );
}

export type AudienceLinks = ReturnType<typeof useAudienceLinks>;
